import ButtonSave from "@View/components/buttons/ButtonSave";
import DefaultInput from "@View/components/formInputs/DefaultInput";
import ModalTittle from "@View/components/ModalTittle";
import ModalWrapper from "@View/components/ModalWrapper";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import getAPI from "@API/index";
import { FetchOnceAutocompleteMultiple } from "@Components/form/control/FetchOnceAutocompleteMultiple";
import { useTranslation } from "react-i18next";
import FormDateRow from "@View/components/formRows/FormDateRow";
import { parseDate, formatDateShort, normalizeDateToUTC } from "@Utils/dateActions";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from "@material-ui/core";

const NewGroupConsolidationModal = ({
  customerId,
  group,
  open,
  handleOpen,
  handleClose,
  reloadData,
}) => {
  const { t, i18n } = useTranslation();
  const customer = useSelector((state) => state.customer?.customer ?? {});

  const [newData, setNewData] = useState({
    name: "",
    startDate: new Date(),
    endDate: new Date(),
  });

  const [clientError, setClientError] = useState(false);
  const [clientVal, setClientVal] = useState([]);
  const [clientDateRanges, setClientDateRanges] = useState({});
  const [isLoadingDateRanges, setIsLoadingDateRanges] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Validation dialog state
  const [validationDialog, setValidationDialog] = useState({
    open: false,
    mismatches: [],
  });

  const handleChange = (value, label) => {
    setNewData({ ...newData, [label]: value });
  };

  // Helper to find date range by client ID (handles GUID format differences)
  const findDateRange = useCallback((clientId, dateRanges) => {
    if (!clientId || !dateRanges) return null;
    const normalizedId = clientId.toLowerCase();
    return dateRanges[clientId] || dateRanges[normalizedId] ||
      Object.entries(dateRanges).find(([key]) => key.toLowerCase() === normalizedId)?.[1];
  }, []);

  // Calculate overlapping date range based on selected clients
  // Returns hasValidOverlap: false if ANY client has no data or if there's no valid overlap
  const calculateOverlappingRange = useCallback((selectedClients, dateRanges) => {
    if (!selectedClients || selectedClients.length === 0) {
      return { minDate: null, maxDate: null, hasValidOverlap: false };
    }

    let overlapStart = null;
    let overlapEnd = null;

    // Check if ALL clients have data
    for (const client of selectedClients) {
      const range = findDateRange(client.id, dateRanges);
      if (!range || !range.minDate || !range.maxDate) {
        // At least one client has no data - no valid overlap possible
        return { minDate: null, maxDate: null, hasValidOverlap: false };
      }

      const clientStart = new Date(range.minDate);
      const clientEnd = new Date(range.maxDate);

      if (overlapStart === null || clientStart > overlapStart) {
        overlapStart = clientStart;
      }
      if (overlapEnd === null || clientEnd < overlapEnd) {
        overlapEnd = clientEnd;
      }
    }

    // Check if there's a valid overlap (start <= end)
    if (overlapStart && overlapEnd && overlapStart > overlapEnd) {
      // No valid overlap - periods don't intersect
      return { minDate: null, maxDate: null, hasValidOverlap: false };
    }

    return { minDate: overlapStart, maxDate: overlapEnd, hasValidOverlap: true };
  }, [findDateRange]);

  // Load date ranges for all customer clients
  const loadClientDateRanges = useCallback(async () => {
    if (!customer.clients || customer.clients.length === 0) return;

    setIsLoadingDateRanges(true);
    try {
      const clientIds = customer.clients.map(c => c.id);
      const response = await getAPI().request({
        method: "post",
        url: `/api/customer/${customerId}/consolidation/client-date-ranges`,
        data: clientIds,
      });

      const data = response.data || response;
      if (data && typeof data === 'object') {
        setClientDateRanges(data);
      }
    } catch (error) {
      console.error("Failed to load client date ranges:", error);
    } finally {
      setIsLoadingDateRanges(false);
    }
  }, [customerId, customer.clients]);

  // Initialize modal when opened
  useEffect(() => {
    if (open && group) {
      // Select all group clients by default
      const defaultClients = group.clients.map((client) => ({
        id: client.id,
        name: client.name,
      }));
      setClientVal(defaultClients);

      // Load date ranges
      loadClientDateRanges();

      // Set initial dates from group period
      const startDateMin = parseDate(group.consolidationPeriod?.startDate, "from") || new Date();
      const endDateMax = parseDate(group.consolidationPeriod?.endDate, "to") || new Date();

      setNewData({
        name: "",
        startDate: startDateMin,
        endDate: endDateMax,
      });
    }
  }, [open, group, loadClientDateRanges]);

  // Update date range when selected clients change
  useEffect(() => {
    if (clientVal.length > 0 && Object.keys(clientDateRanges).length > 0) {
      const { minDate, maxDate, hasValidOverlap } = calculateOverlappingRange(clientVal, clientDateRanges);
      if (hasValidOverlap && minDate && maxDate) {
        setNewData(prev => ({
          ...prev,
          startDate: minDate,
          endDate: maxDate,
        }));
      }
    }
  }, [clientVal, clientDateRanges, calculateOverlappingRange]);

  // Fetch options for autocomplete - all customer clients
  const searchClients = useCallback(async () => {
    return (customer.clients || []).map((client) => ({
      id: client.id,
      name: client.name,
      externalId: client.externalId,
    }));
  }, [customer.clients]);

  const handleClientChange = (newValue) => {
    setClientVal(newValue);
    if (clientError && newValue.length > 0) {
      setClientError(false);
    }
  };

  // Render option with date range
  const renderClientOption = useCallback((option) => {
    const dateRange = findDateRange(option.id, clientDateRanges);
    const dateRangeText = dateRange && dateRange.minDate && dateRange.maxDate
      ? ` (${formatDateShort(dateRange.minDate)} - ${formatDateShort(dateRange.maxDate)})`
      : " (нет данных)";

    const isInGroup = group?.clients?.some(c => c.id === option.id);

    return (
      <span>
        {option.name}
        <span style={{ color: "#888", fontSize: "0.85em" }}>
          {dateRangeText}
          {isInGroup && <span style={{ color: "#4caf50", marginLeft: "8px" }}>(в группе)</span>}
        </span>
      </span>
    );
  }, [findDateRange, clientDateRanges, group?.clients]);

  // Get option label with date range
  const getOptionLabelWithRange = useCallback((option) => {
    const dateRange = findDateRange(option.id, clientDateRanges);
    const dateRangeText = dateRange && dateRange.minDate && dateRange.maxDate
      ? ` (${formatDateShort(dateRange.minDate)} - ${formatDateShort(dateRange.maxDate)})`
      : "";
    return `${option.name}${dateRangeText}`;
  }, [findDateRange, clientDateRanges]);

  // Validate periods before creation
  const validatePeriods = async () => {
    setIsValidating(true);
    try {
      const requestData = {
        clientIds: clientVal.map(c => c.id),
        startDate: normalizeDateToUTC(newData.startDate),
        endDate: normalizeDateToUTC(newData.endDate),
      };

      console.log("Validation request data:", requestData);

      const response = await getAPI().request({
        method: "post",
        url: `/api/customer/${customerId}/consolidation/validate`,
        data: requestData,
      });

      console.log("Validation response:", response);
      const validationData = response.data || response;
      console.log("Validation data:", validationData);
      return validationData;
    } catch (error) {
      console.error("Validation request failed:", error);
      console.error("Error details:", error.response?.data || error.message);
      // Return null to indicate validation error (not just invalid data)
      return null;
    } finally {
      setIsValidating(false);
    }
  };

  // Create consolidation
  const createConsolidation = async (forceCreate = false) => {
    setIsCreating(true);

    const requestData = {
      name: newData.name,
      startDate: normalizeDateToUTC(newData.startDate),
      endDate: normalizeDateToUTC(newData.endDate),
      clients: clientVal,
      forceCreate,
    };

    console.log("Creating consolidation with data:", requestData);
    console.log("Original dates:", { startDate: newData.startDate, endDate: newData.endDate });
    console.log("Normalized dates:", {
      startDate: requestData.startDate?.toISOString(),
      endDate: requestData.endDate?.toISOString()
    });
    console.log("URL:", `/api/customer/${customerId}/groups/${group.id}/consolidation`);

    try {
      const response = await getAPI().request({
        method: "post",
        url: `/api/customer/${customerId}/groups/${group.id}/consolidation`,
        data: requestData,
        successText: "snackbar.success",
      });

      console.log("Consolidation created successfully:", response);

      handleClose();
      if (reloadData) {
        reloadData();
      }
    } catch (error) {
      console.error("Failed to create consolidation:", error);
      console.error("Error response:", error.response?.data || error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (clientVal.length === 0) {
      setClientError(true);
      return;
    }
    if (newData.name.trim().length === 0) {
      return;
    }

    // Validate periods first
    const validation = await validatePeriods();

    console.log("Validation result:", validation);

    // Если валидация вернула null (ошибка запроса), пропускаем и создаем консолидацию
    if (validation === null) {
      console.warn("Validation request failed, creating consolidation anyway");
      await createConsolidation(false);
      return;
    }

    // Если валидация не прошла, показываем диалог
    if (!validation.isValid) {
      if (validation.mismatches && validation.mismatches.length > 0) {
        // Show validation dialog with mismatches
        setValidationDialog({
          open: true,
          mismatches: validation.mismatches,
        });
      } else {
        // Validation failed without specific mismatches - try to create anyway with forceCreate
        console.warn("Validation failed without mismatches, creating consolidation anyway");
        await createConsolidation(false);
      }
      return;
    }

    // If validation passed, create consolidation
    console.log("Validation passed, creating consolidation");
    await createConsolidation(false);
  };

  const handleValidationDialogClose = () => {
    setValidationDialog({ open: false, mismatches: [] });
  };

  const handleForceCreate = async () => {
    setValidationDialog({ open: false, mismatches: [] });
    await createConsolidation(true);
  };

  // Calculate min/max dates for date pickers based on selected clients
  const { minDate: calculatedMinDate, maxDate: calculatedMaxDate, hasValidOverlap } = useMemo(() => {
    if (clientVal.length > 0 && Object.keys(clientDateRanges).length > 0) {
      return calculateOverlappingRange(clientVal, clientDateRanges);
    }
    // No clients selected - no overlap info yet
    return {
      minDate: null,
      maxDate: null,
      hasValidOverlap: false,
    };
  }, [clientVal, clientDateRanges, calculateOverlappingRange]);

  const isLoading = isValidating || isCreating || isLoadingDateRanges;

  return (
    <>
      <ModalWrapper open={open} handleOpen={handleOpen} handleClose={handleClose}>
        <form onSubmit={handleSubmit}>
          <ModalTittle>h1.New Request</ModalTittle>

          <DefaultInput
            value={newData.name}
            label="inputs.GroupName"
            onChange={(value) => handleChange(value, "name")}
          />

          <FetchOnceAutocompleteMultiple
            key={Object.keys(clientDateRanges).length}
            value={clientVal}
            id="ClientCapacity"
            label={t("ClientCapacityTitle")}
            fetchOptions={searchClients}
            onChange={handleClientChange}
            error={clientError}
            helperText={clientError ? t("FieldRequired") : ""}
            renderOptionCustom={renderClientOption}
            getOptionLabelCustom={getOptionLabelWithRange}
          />

          {clientVal.length > 0 && (
            <div style={{
              marginBottom: "16px",
              padding: "8px 12px",
              backgroundColor: hasValidOverlap ? "#f5f5f5" : "#fff3e0",
              borderRadius: "4px",
              fontSize: "0.875rem",
              color: hasValidOverlap ? "#666" : "#e65100"
            }}>
              {hasValidOverlap
                ? `${t("Доступный период")}: ${formatDateShort(calculatedMinDate)} - ${formatDateShort(calculatedMaxDate)}`
                : t("Нет пересекающихся периодов или нет данных у одной из компаний")
              }
            </div>
          )}

          <FormDateRow
            value={newData.startDate}
            label="inputs.Start Date"
            onChange={(_, value) => {
              // Set to first day of the selected month
              const date = new Date(value);
              date.setDate(1);
              handleChange(date, "startDate");
            }}
            dateFormat="dd.MM.yyyy"
            minDate={hasValidOverlap ? calculatedMinDate : undefined}
            maxDate={hasValidOverlap ? calculatedMaxDate : undefined}
            disabled={!hasValidOverlap}
          />

          <FormDateRow
            value={newData.endDate}
            label="inputs.End Date"
            onChange={(_, value) => {
              // Set to last day of the selected month
              const date = new Date(value);
              date.setMonth(date.getMonth() + 1);
              date.setDate(0);
              handleChange(date, "endDate");
            }}
            dateFormat="dd.MM.yyyy"
            minDate={hasValidOverlap ? calculatedMinDate : undefined}
            maxDate={hasValidOverlap ? calculatedMaxDate : undefined}
            disabled={!hasValidOverlap}
          />

          <ButtonSave disabled={isLoading || !hasValidOverlap}>
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "buttons.Save"
            )}
          </ButtonSave>
        </form>
      </ModalWrapper>

      {/* Validation Dialog TODO ПЕРЕДЕЛАТЬ */}
      <Dialog
        open={validationDialog.open}
        onClose={handleValidationDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("Недостаточно данных для консолидации")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Следующие клиенты не имеют данных за выбранный период")}:
          </DialogContentText>
          <ul style={{ margin: "16px 0", paddingLeft: "20px" }}>
            {validationDialog.mismatches.map((mismatch) => (
              <li key={mismatch.clientId} style={{ marginBottom: "8px" }}>
                <strong>{mismatch.clientName}</strong>
                {mismatch.availableStartDate && mismatch.availableEndDate && (
                  <span style={{ color: "#666", marginLeft: "8px" }}>
                    ({t("доступный период")}: {formatDateShort(mismatch.availableStartDate)} - {formatDateShort(mismatch.availableEndDate)})
                  </span>
                )}
              </li>
            ))}
          </ul>
          <DialogContentText>
            {t("Продолжить создание консолидации?")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleValidationDialogClose} color="default">
            {t("buttons.Cancel")}
          </Button>
          <Button onClick={handleForceCreate} color="primary" variant="contained">
            {t("Продолжить")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NewGroupConsolidationModal;
